"use client";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

// ─── Types ───────────────────────────────────────────────────────────────────

interface WelcomeOnboardingModalProps {
  open: boolean;
  onDismiss: () => void;
  planName: string;
  serverLimit: number;
}

// ─── Step definitions ─────────────────────────────────────────────────────────

const STEPS = [
  {
    id: "welcome",
    title: "Welcome to BlockyNetworks!",
    icon: "🎉",
  },
  {
    id: "create-org",
    title: "Step 1: Create your organization",
    icon: "🏗️",
  },
  {
    id: "install-plugin",
    title: "Step 2: Install the plugin",
    icon: "🔌",
  },
  {
    id: "link-server",
    title: "Step 3: Link your server",
    icon: "🔗",
  },
  {
    id: "go-live",
    title: "You're ready to go live!",
    icon: "🚀",
  },
] as const;

type StepId = (typeof STEPS)[number]["id"];

// ─── Step content ─────────────────────────────────────────────────────────────

function StepContent({
  step,
  planName,
  serverLimit,
  onNavigateToNewOrg,
}: {
  step: StepId;
  planName: string;
  serverLimit: number;
  onNavigateToNewOrg: () => void;
}) {
  switch (step) {
    case "welcome":
      return (
        <div className="space-y-4">
          <p className="text-muted-foreground text-sm leading-relaxed">
            Your{" "}
            <span className="font-semibold text-foreground">{planName}</span>{" "}
            plan is active. In the next few minutes, this guide will walk you
            through getting your first Hytale server network live on
            BlockyNetworks.
          </p>
          {serverLimit > 0 && (
            <div className="rounded-md border border-border bg-muted/30 px-4 py-3 text-sm">
              <span className="font-medium text-foreground">
                Your plan supports up to {serverLimit} organization
                {serverLimit !== 1 ? "s" : ""}.
              </span>{" "}
              <span className="text-muted-foreground">
                Each organization maps to one Hytale server community.
              </span>
            </div>
          )}
          <p className="text-muted-foreground text-sm">
            A confirmation email with all the details has been sent to your
            inbox. Let&rsquo;s get started!
          </p>
        </div>
      );

    case "create-org":
      return (
        <div className="space-y-4">
          <p className="text-muted-foreground text-sm leading-relaxed">
            An <span className="font-semibold text-foreground">organization</span>{" "}
            is the hub for your Hytale server community. It holds your server
            connection settings, member list, chat, and analytics.
          </p>
          <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
            <li>Click <span className="font-medium text-foreground">"Create Organization"</span> below (or from your Dashboard).</li>
            <li>Choose a name and slug for your server community.</li>
            <li>Set visibility to <span className="font-medium text-foreground">Public</span> to appear in Server Finder.</li>
          </ol>
          <button
            onClick={onNavigateToNewOrg}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Create Organization →
          </button>
        </div>
      );

    case "install-plugin":
      return (
        <div className="space-y-4">
          <p className="text-muted-foreground text-sm leading-relaxed">
            The <span className="font-semibold text-foreground">BlockyNetwork plugin</span>{" "}
            connects your Hytale server to your organization. It handles
            heartbeats, player data, and server status updates.
          </p>
          <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
            <li>
              Go to{" "}
              <span className="font-medium text-foreground">Downloads</span>{" "}
              and download the BlockyNetwork plugin JAR.
            </li>
            <li>
              Copy the JAR into your Hytale server&rsquo;s{" "}
              <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono">
                plugins/
              </code>{" "}
              folder.
            </li>
            <li>Restart your Hytale server to activate the plugin.</li>
          </ol>
          <div className="rounded-md border border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/20 px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
            Make sure your server is running <span className="font-medium">Hytale 1.0+</span> with the API enabled.
          </div>
        </div>
      );

    case "link-server":
      return (
        <div className="space-y-4">
          <p className="text-muted-foreground text-sm leading-relaxed">
            Link your Hytale server to your BlockyNetworks organization using
            the <span className="font-semibold text-foreground">API key</span>{" "}
            from your organization settings.
          </p>
          <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
            <li>Open your organization&rsquo;s <span className="font-medium text-foreground">Settings</span> page.</li>
            <li>Copy the <span className="font-medium text-foreground">Server API Key</span>.</li>
            <li>
              Paste it into your server&rsquo;s{" "}
              <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono">
                plugins/BlockyNetwork/config.yml
              </code>{" "}
              under <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono">api_key</code>.
            </li>
            <li>Restart the server. Heartbeats will begin within 30 seconds.</li>
          </ol>
        </div>
      );

    case "go-live":
      return (
        <div className="space-y-4">
          <p className="text-muted-foreground text-sm leading-relaxed">
            Once your server is sending heartbeats, it will appear as{" "}
            <span className="font-semibold text-green-600 dark:text-green-400">Online</span>{" "}
            in your dashboard. Players can discover it via the{" "}
            <span className="font-semibold text-foreground">Server Finder</span>.
          </p>
          <div className="rounded-md border border-green-200 bg-green-50/50 dark:border-green-800 dark:bg-green-950/20 px-4 py-3 text-sm text-green-800 dark:text-green-300 space-y-1">
            <p className="font-medium">You&rsquo;re all set!</p>
            <p>Head to your dashboard to monitor your server, manage members, and track analytics.</p>
          </div>
          <p className="text-muted-foreground text-sm">
            Need help? Join our{" "}
            <a
              href="https://discord.gg/blockynetworks"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline font-medium"
            >
              Discord community
            </a>{" "}
            or email{" "}
            <a
              href="mailto:support@blockynetworks.com"
              className="text-primary hover:underline font-medium"
            >
              support@blockynetworks.com
            </a>
            .
          </p>
        </div>
      );
  }
}

// ─── Modal ────────────────────────────────────────────────────────────────────

export function WelcomeOnboardingModal({
  open,
  onDismiss,
  planName,
  serverLimit,
}: WelcomeOnboardingModalProps) {
  const navigate = useNavigate();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  if (!open) return null;

  const currentStep = STEPS[currentStepIndex];
  const isFirst = currentStepIndex === 0;
  const isLast = currentStepIndex === STEPS.length - 1;

  function handleNext() {
    if (isLast) {
      onDismiss();
      navigate("/dashboard");
    } else {
      setCurrentStepIndex((i) => i + 1);
    }
  }

  function handleBack() {
    if (!isFirst) {
      setCurrentStepIndex((i) => i - 1);
    }
  }

  function handleNavigateToNewOrg() {
    onDismiss();
    navigate("/dashboard/my-orgs/new");
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onDismiss}
      />

      {/* Modal */}
      <div className="relative z-50 w-full max-w-lg rounded-xl border border-border bg-background shadow-2xl">

        {/* Progress bar */}
        <div className="h-1 w-full rounded-t-xl overflow-hidden bg-muted">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${((currentStepIndex + 1) / STEPS.length) * 100}%` }}
          />
        </div>

        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-6 pb-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl" aria-hidden="true">
              {currentStep.icon}
            </span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Step {currentStepIndex + 1} of {STEPS.length}
              </p>
              <h2 className="text-lg font-bold text-foreground mt-0.5">
                {currentStep.title}
              </h2>
            </div>
          </div>
          <button
            onClick={onDismiss}
            className="ml-4 flex-shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Step dots */}
        <div className="flex items-center gap-1.5 px-6 mb-4">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-200 ${
                i === currentStepIndex
                  ? "w-6 bg-primary"
                  : i < currentStepIndex
                    ? "w-3 bg-primary/50"
                    : "w-3 bg-muted"
              }`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="px-6 pb-6">
          <StepContent
            step={currentStep.id}
            planName={planName}
            serverLimit={serverLimit}
            onNavigateToNewOrg={handleNavigateToNewOrg}
          />
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between border-t border-border px-6 py-4">
          <button
            onClick={onDismiss}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Skip tour
          </button>
          <div className="flex items-center gap-3">
            {!isFirst && (
              <Button variant="outline" onClick={handleBack}>
                Back
              </Button>
            )}
            <Button onClick={handleNext}>
              {isLast ? "Go to Dashboard" : "Next →"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
